import React from 'react'
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Clock, 
  Calendar,
  Settings,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Shield,
  Smartphone,
  Monitor,
  Target,
  Zap,
  Users,
  BarChart3,
  Star,
  Eye
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Input } from '../../../components/ui/Input'
import { Label } from '../../../components/ui/Label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select'
import { Checkbox } from '../../../components/ui/Checkbox'
import { HelpTooltip } from '../../../components/ui/Tooltip'
import { cn } from '../../../lib/utils'

interface DeliveryChannel {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  recommended: boolean
  setupTime: string
}

const deliveryChannels: DeliveryChannel[] = [
  {
    id: 'email',
    name: 'Email Reports',
    description: 'Comprehensive intelligence reports delivered to your inbox',
    icon: <Mail className="w-5 h-5" />,
    recommended: true,
    setupTime: 'Instant'
  },
  {
    id: 'dashboard',
    name: 'Web Dashboard',
    description: 'Real-time intelligence dashboard accessible anywhere',
    icon: <Monitor className="w-5 h-5" />,
    recommended: true,
    setupTime: 'Instant'
  },
  {
    id: 'alerts',
    name: 'Instant Alerts',
    description: 'Urgent notifications for critical intelligence updates',
    icon: <Bell className="w-5 h-5" />,
    recommended: true,
    setupTime: 'Instant'
  },
  {
    id: 'sms',
    name: 'SMS Notifications',
    description: 'Text message alerts for time-sensitive intelligence',
    icon: <Smartphone className="w-5 h-5" />,
    recommended: false,
    setupTime: '2 min'
  },
  {
    id: 'slack',
    name: 'Slack Integration',
    description: 'Intelligence updates delivered to your Slack channels',
    icon: <MessageSquare className="w-5 h-5" />,
    recommended: false,
    setupTime: '5 min'
  }
]

const frequencyOptions = [
  { value: 'real-time', label: 'Real-time', description: 'Immediate notifications as events occur' },
  { value: 'hourly', label: 'Hourly', description: 'Digest every hour during business hours' },
  { value: 'daily', label: 'Daily', description: 'Morning intelligence briefing' },
  { value: 'weekly', label: 'Weekly', description: 'Comprehensive weekly summary' },
  { value: 'custom', label: 'Custom Schedule', description: 'Set your own delivery times' }
]

const priorityLevels = [
  { value: 'all', label: 'All Intelligence', description: 'Receive all insights and updates' },
  { value: 'high', label: 'High Priority Only', description: 'Only critical insights and urgent alerts' },
  { value: 'critical', label: 'Critical Only', description: 'Emergency alerts and major developments only' }
]

export const IntelligenceDeliveryScreen: React.FC = () => {
  const { data, updateData } = useOnboardingStore()

  const updateDeliveryPreferences = (field: string, value: any) => {
    updateData('deliveryPreferences', {
      ...data.deliveryPreferences,
      [field]: value
    })
  }

  const toggleChannel = (channelId: string) => {
    const currentChannels = data.deliveryPreferences.channels || []
    const updatedChannels = currentChannels.includes(channelId)
      ? currentChannels.filter(id => id !== channelId)
      : [...currentChannels, channelId]
    
    updateDeliveryPreferences('channels', updatedChannels)
  }

  const toggleAlertType = (alertType: string) => {
    const currentTypes = data.deliveryPreferences.alertTypes || []
    const updatedTypes = currentTypes.includes(alertType)
      ? currentTypes.filter(type => type !== alertType)
      : [...currentTypes, alertType]
    
    updateDeliveryPreferences('alertTypes', updatedTypes)
  }

  const selectedChannels = data.deliveryPreferences.channels || []
  const selectedAlertTypes = data.deliveryPreferences.alertTypes || []

  // Calculate completion stats
  const getStats = () => {
    const hasChannels = selectedChannels.length > 0
    const hasFrequency = !!data.deliveryPreferences.frequency
    const hasPriority = !!data.deliveryPreferences.priority
    const hasAlertTypes = selectedAlertTypes.length > 0
    
    const completedSections = [hasChannels, hasFrequency, hasPriority, hasAlertTypes].filter(Boolean).length
    const totalSections = 4
    
    return {
      hasChannels,
      hasFrequency,
      hasPriority,
      hasAlertTypes,
      completionPercentage: Math.round((completedSections / totalSections) * 100)
    }
  }

  const stats = getStats()

  const alertTypes = [
    'Revenue changes',
    'Supply chain disruptions',
    'Competitor activities',
    'Market opportunities',
    'Cost fluctuations',
    'Industry developments',
    'Regulatory changes',
    'Customer sentiment shifts'
  ]

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-green-100 rounded-full">
          <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
            <Bell className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-green-700">Intelligence Delivery</span>
        </div>
        
        <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
          Intelligence Delivery Configuration
          <HelpTooltip text="Choose how and when you want to receive your intelligence insights. Configure delivery channels, frequency, and alert preferences to match your workflow and business needs." />
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Configure how you want to receive your intelligence insights. Choose your preferred channels, 
          timing, and priority levels to ensure you get the right information at the right time.
        </p>

        {/* Progress Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          <div className={cn(
            "bg-white rounded-xl p-4 border shadow-sm transition-colors",
            stats.hasChannels ? "border-green-200 bg-green-50" : "border-gray-200"
          )}>
            <div className="flex items-center gap-2 mb-2">
              {stats.hasChannels ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <Bell className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-semibold text-sm">Channels</span>
            </div>
            <div className="text-xs text-gray-600">
              {stats.hasChannels ? `${selectedChannels.length} selected` : 'Choose delivery'}
            </div>
          </div>
          
          <div className={cn(
            "bg-white rounded-xl p-4 border shadow-sm transition-colors",
            stats.hasFrequency ? "border-green-200 bg-green-50" : "border-gray-200"
          )}>
            <div className="flex items-center gap-2 mb-2">
              {stats.hasFrequency ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <Clock className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-semibold text-sm">Frequency</span>
            </div>
            <div className="text-xs text-gray-600">
              {stats.hasFrequency ? data.deliveryPreferences.frequency : 'Set timing'}
            </div>
          </div>

          <div className={cn(
            "bg-white rounded-xl p-4 border shadow-sm transition-colors",
            stats.hasPriority ? "border-green-200 bg-green-50" : "border-gray-200"
          )}>
            <div className="flex items-center gap-2 mb-2">
              {stats.hasPriority ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <Target className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-semibold text-sm">Priority</span>
            </div>
            <div className="text-xs text-gray-600">
              {stats.hasPriority ? data.deliveryPreferences.priority : 'Set level'}
            </div>
          </div>

          <div className={cn(
            "bg-white rounded-xl p-4 border shadow-sm transition-colors",
            stats.hasAlertTypes ? "border-green-200 bg-green-50" : "border-gray-200"
          )}>
            <div className="flex items-center gap-2 mb-2">
              {stats.hasAlertTypes ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <Eye className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-semibold text-sm">Alerts</span>
            </div>
            <div className="text-xs text-gray-600">
              {stats.hasAlertTypes ? `${selectedAlertTypes.length} types` : 'Choose types'}
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Channels */}
      <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            Delivery Channels
          </CardTitle>
          <CardDescription className="text-base">
            Select how you want to receive your intelligence insights and alerts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {deliveryChannels.map((channel) => {
              const isSelected = selectedChannels.includes(channel.id)
              
              return (
                <Card 
                  key={channel.id}
                  className={cn(
                    "cursor-pointer transition-all duration-300 hover:shadow-lg border-2 relative overflow-hidden",
                    isSelected 
                      ? "border-green-500 bg-green-50 shadow-lg" 
                      : "border-gray-200 hover:border-green-300"
                  )}
                  onClick={() => toggleChannel(channel.id)}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-4 right-4 z-10">
                      <CheckCircle2 className="w-5 h-5 text-green-600 fill-green-100" />
                    </div>
                  )}

                  {/* Recommended badge */}
                  {channel.recommended && (
                    <div className="absolute top-4 left-4 z-10">
                      <div className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded">
                        Recommended
                      </div>
                    </div>
                  )}

                  <CardContent className="p-6 pt-12">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center transition-colors",
                          isSelected 
                            ? "bg-green-100 text-green-600" 
                            : "bg-gray-100 text-gray-500"
                        )}>
                          {channel.icon}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-lg mb-2">{channel.name}</div>
                          <p className="text-sm text-gray-600 leading-relaxed">{channel.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Clock className="w-3 h-3" />
                          {channel.setupTime}
                        </div>
                        {channel.recommended && (
                          <div className="flex items-center gap-1 text-sm text-blue-600">
                            <Star className="w-3 h-3" />
                            Popular choice
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Delivery Preferences */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Frequency & Timing */}
        <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              Frequency & Timing
            </CardTitle>
            <CardDescription className="text-base">
              Choose when and how often you want to receive intelligence updates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="frequency" className="flex items-center gap-2 font-medium">
                <Calendar className="w-4 h-4 text-gray-500" />
                Delivery Frequency
              </Label>
              <Select 
                value={data.deliveryPreferences.frequency} 
                onValueChange={(value) => updateDeliveryPreferences('frequency', value)}
              >
                <SelectTrigger className="h-12 text-base">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  {frequencyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="py-3">
                      <div className="space-y-1">
                        <div className="font-medium">{option.label}</div>
                        <div className="text-sm text-gray-500">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label htmlFor="priority" className="flex items-center gap-2 font-medium">
                <Target className="w-4 h-4 text-gray-500" />
                Priority Level
              </Label>
              <Select 
                value={data.deliveryPreferences.priority} 
                onValueChange={(value) => updateDeliveryPreferences('priority', value)}
              >
                <SelectTrigger className="h-12 text-base">
                  <SelectValue placeholder="Select priority level" />
                </SelectTrigger>
                <SelectContent>
                  {priorityLevels.map((level) => (
                    <SelectItem key={level.value} value={level.value} className="py-3">
                      <div className="space-y-1">
                        <div className="font-medium">{level.label}</div>
                        <div className="text-sm text-gray-500">{level.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {data.deliveryPreferences.frequency && (
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-start gap-3">
                  <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-blue-800 mb-1">Delivery Schedule Set</div>
                    <div className="text-sm text-blue-700">
                      You'll receive {data.deliveryPreferences.frequency} intelligence updates 
                      {data.deliveryPreferences.priority && ` at ${data.deliveryPreferences.priority} priority level`}.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alert Types */}
        <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              Alert Types
            </CardTitle>
            <CardDescription className="text-base">
              Select which types of intelligence updates you want to receive
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {alertTypes.map((alertType) => {
                const isSelected = selectedAlertTypes.includes(alertType)
                
                return (
                  <div
                    key={alertType}
                    className={cn(
                      "flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200",
                      isSelected 
                        ? "border-orange-500 bg-orange-50 shadow-sm" 
                        : "border-gray-200 hover:border-orange-300 hover:bg-orange-50/30"
                    )}
                    onClick={() => toggleAlertType(alertType)}
                  >
                    <Checkbox
                      id={`alert-${alertType}`}
                      checked={isSelected}
                      onChange={() => {}} // Controlled by parent click
                      className="data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
                    />
                    <Label 
                      htmlFor={`alert-${alertType}`}
                      className="font-medium cursor-pointer flex-1"
                    >
                      {alertType}
                    </Label>
                  </div>
                )
              })}
            </div>

            {selectedAlertTypes.length > 0 && (
              <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-orange-800 mb-1">Alert Monitoring Active</div>
                    <div className="text-sm text-orange-700">
                      You'll receive alerts for <strong>{selectedAlertTypes.length} intelligence categories</strong> 
                      when significant developments occur.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Intelligence Preview */}
      {stats.completionPercentage > 50 && (
        <div className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-green-800 mb-3">Intelligence Delivery Preview</h3>
              <p className="text-green-700 mb-6">
                Based on your delivery configuration, here's how you'll receive your intelligence:
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-green-800">
                    <Bell className="w-5 h-5" />
                    Delivery Channels ({selectedChannels.length})
                  </div>
                  <ul className="space-y-2 text-sm text-green-700 ml-7">
                    {selectedChannels.map((channelId) => {
                      const channel = deliveryChannels.find(c => c.id === channelId)
                      return (
                        <li key={channelId}>• {channel?.name}</li>
                      )
                    })}
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-green-800">
                    <Target className="w-5 h-5" />
                    Alert Types ({selectedAlertTypes.length})
                  </div>
                  <ul className="space-y-2 text-sm text-green-700 ml-7">
                    {selectedAlertTypes.slice(0, 4).map((alertType) => (
                      <li key={alertType}>• {alertType}</li>
                    ))}
                    {selectedAlertTypes.length > 4 && (
                      <li>• +{selectedAlertTypes.length - 4} more alert types</li>
                    )}
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-white/50 rounded-xl border border-green-200/50">
                <div className="text-sm text-green-700 font-medium">
                  🚀 Perfect! Your intelligence delivery is configured. Final step: Review your complete setup.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Status */}
      <div className={cn(
        "p-6 rounded-xl border-2 transition-all duration-300",
        stats.completionPercentage >= 80 ? "bg-green-50 border-green-200" :
        stats.completionPercentage >= 50 ? "bg-yellow-50 border-yellow-200" :
        "bg-gray-50 border-gray-200"
      )}>
        <div className="flex items-center gap-4">
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center",
            stats.completionPercentage >= 80 ? "bg-green-100" :
            stats.completionPercentage >= 50 ? "bg-yellow-100" :
            "bg-gray-100"
          )}>
            {stats.completionPercentage >= 80 ? (
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            ) : (
              <Bell className="w-6 h-6 text-gray-600" />
            )}
          </div>
          <div>
            <div className="text-lg font-semibold mb-1">
              Intelligence Delivery: {stats.completionPercentage}% Complete
            </div>
            <div className="text-sm text-gray-600">
              {stats.completionPercentage >= 80 && "Ready for final review! "}
              {stats.hasChannels && `${selectedChannels.length} channels configured`}
              {stats.hasChannels && (stats.hasFrequency || stats.hasAlertTypes) && " • "}
              {stats.hasFrequency && `${data.deliveryPreferences.frequency} delivery`}
              {stats.hasFrequency && stats.hasAlertTypes && " • "}
              {stats.hasAlertTypes && `${selectedAlertTypes.length} alert types`}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}